import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Settings, ArrowLeft, Plus, Link2, Trash2, Pencil, Globe,
  Check, Zap, AlertCircle, Loader2, MapPin
} from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/toastContext';
import { useConfirm } from '../components/ui/useConfirm';

interface LLMConfigItem {
  id: string;
  name: string;
  provider: string;
  model: string;
  api_key?: string;
  base_url?: string;
  temperature?: number;
  max_tokens?: number;
  is_global?: boolean;
  is_vision_default?: boolean;
}

interface ProviderMeta {
  id: string;
  label: string;
  region: string;
  type: string;
  default_base_url: string | null;
  env_key: string | null;
  models: { display: string; id: string }[];
}

const DEFAULT_FORM = {
  name: '',
  provider: 'openai',
  model: 'gpt-4o',
  api_key: '',
  base_url: '',
  temperature: 0.7,
  max_tokens: 4096,
  is_vision_default: false,
};

/** 提取后端错误文案（detail 优先，其次 Error.message） */
const errorMessage = (e: unknown): string => {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (typeof err.message === 'string' && err.message) return err.message;
  return '未知错误';
};

export default function LLMConfig() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();

  const [configs, setConfigs] = useState<LLMConfigItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...DEFAULT_FORM });
  const [providers, setProviders] = useState<ProviderMeta[]>([]);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success?: boolean; reply?: string; error?: string } | null>(null);
  const [loadingProviders, setLoadingProviders] = useState(true);

  const loadConfigs = async () => {
    try {
      const res = await apiClient.get('/llm-configs');
      setConfigs(res.data);
    } catch (e) {
      console.error('Failed to load LLM configs:', e);
    }
  };

  const loadProviders = async () => {
    try {
      setLoadingProviders(true);
      const res = await apiClient.get('/llm-configs/providers/models');
      setProviders(res.data.providers || []);
    } catch (e) {
      console.error('Failed to load providers:', e);
      setProviders([
        { id: 'openai', label: 'OpenAI', region: 'global', type: 'openai-compat', default_base_url: 'https://api.openai.com/v1', env_key: 'OPENAI_API_KEY', models: [{ display: 'GPT-4o', id: 'gpt-4o' }, { display: 'GPT-4o-mini', id: 'gpt-4o-mini' }] },
        { id: 'anthropic', label: 'Anthropic Claude', region: 'global', type: 'native', default_base_url: 'https://api.anthropic.com', env_key: 'ANTHROPIC_API_KEY', models: [{ display: 'Claude Sonnet 4', id: 'claude-sonnet-4-20250514' }] },
        { id: 'deepseek', label: 'DeepSeek', region: 'cn', type: 'openai-compat', default_base_url: 'https://api.deepseek.com/v1', env_key: 'DEEPSEEK_API_KEY', models: [{ display: 'DeepSeek-V3', id: 'deepseek-chat' }, { display: 'DeepSeek-R1', id: 'deepseek-reasoner' }] },
        { id: 'jdcloud', label: '京东云', region: 'cn', type: 'openai-compat', default_base_url: 'https://modelservice.jdcloud.com/coding/openai/v1', env_key: 'JDCLOUD_API_KEY', models: [{ display: '京东云 DeepSeek', id: 'deepseek-r1' }] },
        { id: 'siliconflow', label: '硅基流动', region: 'cn', type: 'openai-compat', default_base_url: 'https://api.siliconflow.cn/v1', env_key: 'SILICONFLOW_API_KEY', models: [{ display: 'SiliconFlow/Qwen2.5', id: 'Qwen/Qwen2.5-72B-Instruct' }] },
        { id: 'moonshot', label: '月之暗面 Kimi', region: 'cn', type: 'openai-compat', default_base_url: 'https://api.moonshot.cn/v1', env_key: 'MOONSHOT_API_KEY', models: [{ display: 'Moonshot-v1-8k', id: 'moonshot-v1-8k' }] },
        { id: 'zhipu', label: '智谱 GLM', region: 'cn', type: 'openai-compat', default_base_url: 'https://open.bigmodel.cn/api/paas/v4', env_key: 'ZHIPU_API_KEY', models: [{ display: 'GLM-4', id: 'glm-4' }] },
        { id: 'dashscope', label: '阿里云 DashScope', region: 'cn', type: 'openai-compat', default_base_url: 'https://dashscope.aliyuncs.com/compatible-mode/v1', env_key: 'DASHSCOPE_API_KEY', models: [{ display: 'Qwen-Max', id: 'qwen-max' }] },
        { id: 'volcengine', label: '火山引擎', region: 'cn', type: 'openai-compat', default_base_url: 'https://ark.cn-beijing.volces.com/api/v3', env_key: 'VOLCENGINE_API_KEY', models: [{ display: 'Doubao-Pro', id: 'doubao-pro-32k' }] },
        { id: 'exgate', label: 'ExGate', region: 'any', type: 'openai-compat', default_base_url: 'http://localhost:25500/v1', env_key: 'EXGATE_API_KEY', models: [{ display: 'ExGate Default', id: 'exgate-default' }, { display: 'ExGate GPT-4o', id: 'gpt-4o' }] },
        { id: 'custom', label: '自定义', region: 'any', type: 'openai-compat', default_base_url: null, env_key: null, models: [] },
      ]);
    } finally {
      setLoadingProviders(false);
    }
  };

  useEffect(() => {
    // 通过本地包装触发异步加载，避免在 effect 体内直接同步调用 setState 闭包
    const tick = () => {
      void loadConfigs();
      void loadProviders();
    };
    tick();
  }, [agentId]);

  const getCurrentProvider = useCallback(() => {
    return providers.find(p => p.id === form.provider);
  }, [providers, form.provider]);

  const getAvailableModels = useCallback(() => {
    const p = getCurrentProvider();
    return p?.models || [];
  }, [getCurrentProvider]);

  const handleSelectProvider = (providerId: string) => {
    const p = providers.find(pr => pr.id === providerId);
    const models = p?.models || [];
    const defaultModel = models[0]?.id || '';
    setForm(prev => ({
      ...prev,
      provider: providerId,
      base_url: p?.default_base_url || '',
      model: defaultModel,
    }));
    setTestResult(null);
  };

  const validate = () => {
    if (!form.name.trim()) {
      toast.error('配置名称为必填项');
      return false;
    }
    if (!form.api_key.trim()) {
      toast.error('API Key 为必填项');
      return false;
    }
    if (!form.model.trim()) {
      toast.error('Model 为必填项');
      return false;
    }
    return true;
  };

  const createConfig = async () => {
    if (!validate()) return;
    try {
      const res = await apiClient.post('/llm-configs', form);
      setConfigs([res.data, ...configs]);
      closeForm();
      toast.success('LLM 配置已创建');
    } catch (e) {
      toast.error('创建失败: ' + errorMessage(e));
      console.error('Failed to create LLM config:', e);
    }
  };

  const startEdit = (config: LLMConfigItem) => {
    setEditId(config.id);
    setForm({
      name: config.name,
      provider: config.provider,
      model: config.model,
      api_key: config.api_key || '',
      base_url: config.base_url || '',
      temperature: config.temperature ?? 0.7,
      max_tokens: config.max_tokens ?? 4096,
      is_vision_default: config.is_vision_default ?? false,
    });
    setTestResult(null);
    setShowForm(true);
  };

  const saveEdit = async () => {
    if (!editId) return;
    if (!validate()) return;
    try {
      const res = await apiClient.put(`/llm-configs/${editId}`, form);
      setConfigs(configs.map(c => c.id === editId ? res.data : c));
      closeForm();
      toast.success('LLM 配置已更新');
    } catch (e) {
      toast.error('更新失败: ' + errorMessage(e));
      console.error('Failed to update LLM config:', e);
    }
  };

  const deleteConfig = async (id: string) => {
    if (!(await confirm({ title: '确定要删除这个 LLM 配置吗？', danger: true }))) return;
    try {
      await apiClient.delete(`/llm-configs/${id}`);
      setConfigs(prev => prev.filter(c => c.id !== id));
      if (selectedId === id) setSelectedId(null);
      await loadConfigs();
      toast.success('LLM 配置已删除');
    } catch (e) {
      toast.error('删除失败: ' + errorMessage(e));
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditId(null);
    setForm({ ...DEFAULT_FORM });
    setTestResult(null);
  };

  const testConnection = async () => {
    if (!form.api_key.trim()) {
      toast.error('请先填写 API Key');
      return;
    }
    if (!form.model.trim()) {
      toast.error('请先填写 Model');
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await apiClient.post('/llm-configs/test', {
        provider: form.provider,
        api_key: form.api_key,
        model: form.model,
        base_url: form.base_url || undefined,
        temperature: form.temperature,
        max_tokens: form.max_tokens,
      });
      setTestResult(res.data);
      if (res.data.success) {
        toast.success('连接测试成功！');
      } else {
        toast.error('连接测试失败: ' + res.data.error);
      }
    } catch (e) {
      const errMsg = errorMessage(e);
      setTestResult({ success: false, error: errMsg });
      toast.error('连接测试失败: ' + errMsg);
    } finally {
      setTesting(false);
    }
  };

  const assignToAgent = async () => {
    if (!selectedId || !agentId) return;
    try {
      await apiClient.put(`/agents/${agentId}`, { llm_config_id: selectedId });
      toast.success('LLM 配置已绑定到 Agent');
      navigate(`/agents/${agentId}`);
    } catch (e) {
      toast.error('绑定 LLM 配置失败');
      console.error('Failed to assign config:', e);
    }
  };

  const cnProviders = providers.filter(p => p.region === 'cn');
  const globalProviders = providers.filter(p => p.region === 'global');
  const customProviders = providers.filter(p => p.region === 'any');

  const renderProviderCard = (p: ProviderMeta) => {
    const isSelected = form.provider === p.id;
    return (
      <button
        key={p.id}
        type="button"
        onClick={() => handleSelectProvider(p.id)}
        className="card"
        style={{
          padding: '12px 16px',
          textAlign: 'left',
          cursor: 'pointer',
          border: isSelected ? '2px solid var(--color-primary)' : '1px solid var(--border-default)',
          background: isSelected ? 'var(--bg-tertiary)' : 'var(--bg-secondary)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: isSelected ? 'var(--color-primary)' : 'var(--bg-tertiary)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {isSelected ? <Check size={16} color="#fff" /> : <Zap size={16} />}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{p.label}</div>
          <div className="text-muted" style={{ fontSize: 12 }}>
            {p.type === 'native' ? '原生 API' : 'OpenAI 兼容'}
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="container">
      <button className="secondary mb-4" onClick={() => navigate(`/agents/${agentId}`)}>
        <ArrowLeft size={16} /> 返回概览
      </button>

      <div className="card mb-4">
        <div className="flex items-center justify-between">
          <h2><Settings size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />LLM 配置</h2>
          <button className="primary" onClick={() => { setShowForm(true); setEditId(null); setForm({ ...DEFAULT_FORM }); setTestResult(null); }}>
            <Plus size={14} /> 新建配置
          </button>
        </div>
        <p>配置 LLM 提供商，以便 Agent 能够进行对话和推理。</p>
      </div>

      {/* Form */}
      {showForm && (
        <div className="card mb-4">
          <h3>{editId ? '编辑 LLM 配置' : '创建新 LLM 配置'}</h3>

          <div className="flex flex-col" style={{ gap: 20 }}>
            {/* Name */}
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>配置名称</label>
              <input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="如：Claude 4.0 主力"
              />
            </div>

            {/* Provider Selection */}
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 8 }}>选择提供商</label>
              {loadingProviders ? (
                <div className="flex items-center gap-2 text-muted">
                  <Loader2 size={16} className="spin" /> 加载提供商列表...
                </div>
              ) : (
                <>
                  {globalProviders.length > 0 && (
                    <>
                      <div className="text-muted text-sm" style={{ marginBottom: 8, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}><Globe size={14} /> 国际</div>
                      <div className="grid grid-3" style={{ marginBottom: 16 }}>
                        {globalProviders.map(renderProviderCard)}
                      </div>
                    </>
                  )}
                  {cnProviders.length > 0 && (
                    <>
                      <div className="text-muted text-sm" style={{ marginBottom: 8, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}><MapPin size={14} /> 国内</div>
                      <div className="grid grid-3" style={{ marginBottom: 16 }}>
                        {cnProviders.map(renderProviderCard)}
                      </div>
                    </>
                  )}
                  {customProviders.length > 0 && (
                    <>
                      <div className="text-muted text-sm" style={{ marginBottom: 8, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}><Settings size={14} /> 其他</div>
                      <div className="grid grid-3">
                        {customProviders.map(renderProviderCard)}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            {/* Model — free input with datalist suggestions */}
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>Model</label>
              <input
                list="model-suggestions"
                value={form.model}
                onChange={e => setForm({ ...form, model: e.target.value })}
                placeholder="输入或选择模型 ID，如 gpt-4o / deepseek-chat / glm-4"
              />
              <datalist id="model-suggestions">
                {getAvailableModels().map(m => (
                  <option key={m.id} value={m.id} label={m.display} />
                ))}
              </datalist>
              <p className="text-muted text-sm" style={{ margin: '4px 0 0' }}>
                可直接输入模型 ID，或从提示中选择。切换 Provider 会自动更新推荐列表。
              </p>
            </div>

            {/* API Key */}
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
                API Key
                {getCurrentProvider()?.env_key && (
                  <span className="text-muted" style={{ fontSize: 12, marginLeft: 8 }}>
                    (环境变量: {getCurrentProvider()?.env_key})
                  </span>
                )}
              </label>
              <input
                type="password"
                value={form.api_key}
                onChange={e => setForm({ ...form, api_key: e.target.value })}
                placeholder="sk-..."
              />
            </div>

            {/* Base URL */}
            <div>
              <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>
                Base URL
                {getCurrentProvider()?.default_base_url && (
                  <span className="text-muted" style={{ fontSize: 12, marginLeft: 8 }}>
                    (已自动填充)
                  </span>
                )}
              </label>
              <input
                value={form.base_url}
                onChange={e => setForm({ ...form, base_url: e.target.value })}
                placeholder="https://api.example.com/v1"
              />
              <p className="text-muted text-sm" style={{ margin: '4px 0 0' }}>
                国内模型服务通常提供 OpenAI 兼容接口，选择提供商后会自动填充。如有特殊需求可手动修改。
              </p>
            </div>

            {/* Temperature & Max Tokens */}
            <div className="flex gap-4">
              <div style={{ flex: 1 }}>
                <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>Temperature</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="2"
                  value={form.temperature}
                  onChange={e => setForm({ ...form, temperature: parseFloat(e.target.value) })}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>Max Tokens</label>
                <input
                  type="number"
                  value={form.max_tokens}
                  onChange={e => setForm({ ...form, max_tokens: parseInt(e.target.value) })}
                />
              </div>
            </div>

            {/* 视觉默认标记 */}
            <label className="flex items-center gap-2" style={{ cursor: 'pointer', marginTop: 8 }}>
              <input
                type="checkbox"
                checked={form.is_vision_default}
                onChange={e => setForm({ ...form, is_vision_default: e.target.checked })}
              />
              <span className="text-sm">设为视觉默认模型（有视觉需求且未特别指定时使用，作用域内唯一）</span>
            </label>

            {/* Test Connection */}
            <div className="card" style={{ background: 'var(--bg-tertiary)', padding: 16 }}>
              <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
                <div className="flex items-center gap-2">
                  <Zap size={16} />
                  <span style={{ fontWeight: 600 }}>连接测试</span>
                </div>
                <button
                  className="secondary"
                  onClick={testConnection}
                  disabled={testing}
                  style={{ padding: '6px 12px', fontSize: 13 }}
                >
                  {testing ? <><Loader2 size={14} className="spin" style={{ display: 'inline', marginRight: 4 }} /> 测试中...</> : <><Zap size={14} style={{ display: 'inline', marginRight: 4 }} /> 测试连接</>}
                </button>
              </div>
              {testResult && (
                <div
                  className={`badge ${testResult.success ? 'success' : 'error'}`}
                  style={{ display: 'block', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {testResult.success ? (
                    <><Check size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> 连接成功！回复: {testResult.reply}</>
                  ) : (
                    <><AlertCircle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> 连接失败: {testResult.error}</>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2 mt-2">
              <button className="primary" onClick={editId ? saveEdit : createConfig}>
                {editId ? '保存' : '创建'}
              </button>
              <button className="secondary" onClick={closeForm}>取消</button>
            </div>
          </div>
        </div>
      )}

      {/* Config List */}
      <div className="card">
        <h3>可用配置</h3>
        {configs.length === 0 ? (
          <div className="empty">
            <p>暂无 LLM 配置，请创建新配置</p>
          </div>
        ) : (
          <div className="list">
            {configs.map(config => (
              <div
                key={config.id}
                className="list-item"
                style={{
                  borderColor: selectedId === config.id ? 'var(--color-primary)' : undefined,
                  cursor: 'pointer'
                }}
                onClick={() => setSelectedId(config.id)}
              >
                <div className="list-item-content">
                  <div className="flex items-center gap-2">
                    <h3 style={{ margin: 0 }}>{config.name}</h3>
                    <span className="badge info">{config.provider}</span>
                    {config.is_global && <span className="badge success">全局默认</span>}
                    {config.is_vision_default && <span className="badge info">视觉默认</span>}
                  </div>
                  <p style={{ margin: '4px 0 0' }}>
                    {config.model} · Temperature: {config.temperature}
                    {config.base_url && <span className="text-muted"> · <Globe size={12} style={{ verticalAlign: 'middle' }} /> {config.base_url}</span>}
                  </p>
                </div>
                <div className="flex gap-2 items-center">
                  {selectedId === config.id && (
                    <span className="badge success">已选择</span>
                  )}
                  <button
                    className="secondary"
                    onClick={(e) => { e.stopPropagation(); startEdit(config); }}
                    style={{ padding: '4px 8px' }}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    className="secondary"
                    onClick={(e) => { e.stopPropagation(); deleteConfig(config.id); }}
                    style={{ padding: '4px 8px' }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {configs.length > 0 && (
          <button
            className="primary mt-4"
            onClick={assignToAgent}
            disabled={!selectedId}
            style={{ width: '100%' }}
          >
            <Link2 size={14} /> 绑定到当前 Agent
          </button>
        )}
      </div>
      {dialog}
    </div>
  );
}
